/**
 * editor2/modals/PaymentEmailModal.tsx
 * 支払い確認メール送信モーダル
 * Step 1: 言語選択 (ja/en/tw/kr)
 * Step 2: プレビュー表示 (to/subject/body) → 送信
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import type { EditorReservation } from '../types';

export interface PaymentEmailState {
  open: boolean;
  step: 'lang' | 'preview';
  type: string; // 'payment' | 'free' | 'request_ok' | 'request_full' | 'request_offer' | 'comment'
  to: string;
  subject: string;
  body: string;
  sending: boolean;
  error: string;
}

export const INITIAL_PAYMENT_EMAIL_STATE: PaymentEmailState = {
  open: false,
  step: 'lang',
  type: 'payment',
  to: '',
  subject: '',
  body: '',
  sending: false,
  error: '',
};

interface PaymentEmailModalProps {
  open: boolean;
  editing: EditorReservation;
  sysApiBase: string;
  onClose: () => void;
  onSend: (emailData: { to: string; subject: string; body: string }) => void;
  // State management delegated to parent/hook
  state: PaymentEmailState;
  onStateChange: (updater: (prev: PaymentEmailState) => PaymentEmailState) => void;
  onLangSelect: (lang: string) => void;
}

function getEmailTypeTitle(type: string, step: string): string {
  if (step === 'preview') return 'メールプレビュー';
  switch (type) {
    case 'payment':
      return '支払い確認メールを作成しますか？';
    case 'request_ok':
      return 'リクエスト承認メールを作成しますか？';
    case 'request_full':
      return 'リクエスト満車回答メールを作成しますか？';
    case 'request_offer':
      return 'リクエスト代替案メールを作成しますか？';
    case 'comment':
      return 'コメント返信メールを作成しますか？';
    default:
      return 'フリーメールを作成しますか？';
  }
}

export default function PaymentEmailModal({
  open,
  state,
  onClose,
  onSend,
  onStateChange,
  onLangSelect,
}: PaymentEmailModalProps) {
  const title = getEmailTypeTitle(state.type, state.step);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {state.step === 'lang' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('ja')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              日本語
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('en')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              英語
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('tw')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              中国語（繁体字）
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('kr')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              韓国語
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="宛先"
              fullWidth
              size="small"
              value={state.to}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, to: e.target.value }))
              }
            />
            <TextField
              label="件名"
              fullWidth
              size="small"
              value={state.subject}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, subject: e.target.value }))
              }
            />
            <TextField
              label="本文"
              fullWidth
              multiline
              rows={10}
              value={state.body}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, body: e.target.value }))
              }
            />
            {state.error && (
              <Typography variant="caption" color="error">
                {state.error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          いいえ
        </Button>
        {state.step === 'preview' && (
          <Button
            onClick={() => onSend({ to: state.to, subject: state.subject, body: state.body })}
            variant="contained"
            disabled={state.sending}
            startIcon={
              state.sending ? <CircularProgress size={20} color="inherit" /> : undefined
            }
          >
            送信する
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
